import { AriaLabelingProps, DOMProps } from '@react-types/shared';
import React, { ForwardedRef } from 'react';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import styles from '@danske/sapphire-css/components/modalLayout/modalLayout.module.css';
import clsx from 'clsx';
import { AriaLabelingPropsKeys, GlobalDomAttributes } from '../../utils/types';

export interface ModalLayoutProps
  extends DOMProps,
    Pick<AriaLabelingProps, AriaLabelingPropsKeys>,
    SapphireStyleProps,
    GlobalDomAttributes {
  /**
   * Content placed in the "header" slot. Typically, a {@link ModalLayout.Header} element.
   *
   * @example
   * ```
   * <ModalLayout
   *   header={
   *     <ModalLayout.Header heading="Heading" subheading="Subheading" />
   *   }
   * />
   * ```
   */
  header: React.ReactNode;
  /**
   * Scrollable content placed in the "body" slot. Typically, a {@link ModalLayout.Body}
   * element.
   *
   * @example
   * ```
   * <ModalLayout body={<ModalLayout.Body>...</ModalLayout.Body>} />
   * ```
   */
  body: React.ReactNode;

  /**
   * Content placed in the "footer" slot. Typically, a {@link ModalLayout.Footer} element.
   *
   * @example
   * ```
   * <ModalLayout
   *   footer={
   *     <ModalLayout.Footer>
   *       <ButtonGroup>...</ButtonGroup>
   *     </ModalLayout.Footer>
   *   }
   * />
   * ```
   */
  footer?: React.ReactNode;
}
export const ModalLayout = React.forwardRef(function ModalLayout(
  { header, body, footer, ...props }: ModalLayoutProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();
  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  return (
    <div
      className={clsx(styles['sapphire-modal-layout'], styleProps.className)}
      style={styleProps.style}
      {...filteredProps}
      ref={ref}
    >
      {header && (
        <div className={styles['sapphire-modal-layout__header-container']}>
          {header}
        </div>
      )}
      {body}
      {footer && (
        <div className={styles['sapphire-modal-layout__footer-container']}>
          {footer}
        </div>
      )}
    </div>
  );
});
